import { OrderDetails } from '@/models/order-details/order-details';
import { Component, OnInit } from '@angular/core';
import { AppService } from '@services/app.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ConfigService } from '@services/config.service';
import { Router } from '@angular/router';
import * as CryptoJS from 'crypto-js';


@Component({
  selector: 'app-order-processing',
  templateUrl: './order-processing.component.html',
  styleUrl: './order-processing.component.scss'
})
export class OrderProcessingComponent implements OnInit {
  orders: OrderDetails[] = [];
  selectedStatus = 'PLACED';
  selectedOrder: any;
  orderItems: any[] = [];
  isItemsModalOpen = false;
  searchText: any;
  getImageId: number = 0;
  endpoint: string = "";
  private secretKey = "#EVOL-&$%$DF%SION*^%#^*F&%^DFSEYUJ";

  constructor(
    private spinner: NgxSpinnerService,
    private appService: AppService,
    private toastr: ToastrService,
    private config: ConfigService,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.loadOrdersByStatus();
    this.endpoint = this.config.getConfig().END_POINT;
  }

  /* LOAD ORDERS */
  loadOrdersByStatus() {
    this.spinner.show();
    this.appService.getByStatusOrderDetails(this.selectedStatus).subscribe({
      next: (res: OrderDetails[]) => {
        this.orders = res.sort((a,b) => b.orderId - a.orderId) || [];
        this.spinner.hide();
      },
      error: (err) => {
        console.error(err);
        this.spinner.hide();
        this.toastr.error('Failed to load orders');
      }
    });
  }

  /* OPEN POPUP */
  openItemsModal(order: any) {
    this.selectedOrder = order;
    this.orderItems = order.orderItems || [];
    this.orderItems.forEach(item => {
      if (item.productVariantId) {
        this.variantIdBasedImageDetailsGetting(item);
      }
    });
    this.isItemsModalOpen = true;
    document.body.style.overflow = 'hidden';
  }

  /* CLOSE MODAL */
  closeItemsModal() {
    this.isItemsModalOpen = false;
    document.body.style.overflow = 'auto';
  }

  /* UPDATE SINGLE ITEM */
  updateItem(item: any) {
    if (item.orderStatus == '') {
      this.toastr.error('Select a order status');
      return;
    }
    const jsonData = {
      orderItemId: item.orderItemId,
      orderStatus: item.orderStatus
    };
    this.spinner.show();
    this.appService.updateOrderItemStatus(jsonData).subscribe({
      next: () => {
        this.spinner.hide();
        this.toastr.success('Item updated');
      },
      error: (error: any) => {
        this.spinner.hide();
        this.toastr.error(error?.error?.message, 'Update failed');
      }
    });
  }

  /* UPDATE ALL ITEMS */
  updateAllItems() {
    const jsonData = {
      orderId: this.selectedOrder.orderId,
      orderStatus: 'PROCESSING',
      trackingNumber: ''
    };
    this.spinner.show();
    this.appService.updateBulkOrderItems(jsonData).subscribe({
      next: () => {
        this.spinner.hide();
        this.toastr.success('All items updated');
        this.closeItemsModal();
        this.loadOrdersByStatus();
      },
      error: () => {
        this.spinner.hide();
        this.toastr.error('Bulk update failed');
      }
    });
  }

  variantIdBasedImageDetailsGetting(item: any) {
  this.spinner.show();
  this.appService.variantIdBasedImageDetailsGetting(item.productVariantId).subscribe({
    next: (res: any) => {
      this.spinner.hide();

      // store imageId inside that row
      item.imageDetailsId = res.imageDetailsId;
    },
    error: () => {
      this.spinner.hide();
    }
  });
}

  getImageById(imageId: number): string {
    return imageId
      ? `${this.endpoint}/api/product/image-details/get/${imageId}`
      : 'assets/img/my-1.jpg';
  }

  /* TOTAL ITEM COUNT */
  getItemCount(order: any): number {
    return order.orderItems?.length || 0;
  }

  groupItemsByCategory(items: any[]) {
    const grouped: any = {};
    items.forEach(item => {
      if (!grouped[item.categoryName]) {
        grouped[item.categoryName] = {};
      }
      if (!grouped[item.categoryName][item.subCategoryName]) {
        grouped[item.categoryName][item.subCategoryName] = [];
      }
      grouped[item.categoryName][item.subCategoryName].push(item);
    });
    return grouped;
  }

  downloadPDF() {
    let allItems: any[] = [];
    this.orders.forEach((order: any) => {
      order.orderItems?.forEach((item: any) => allItems.push(item));
    });

    if (allItems.length === 0) {
      this.toastr.warning('No data to export');
      return;
    }

    const grouped = this.groupItemsByCategory(allItems);
    const doc = new jsPDF('p', 'mm', 'a4');
    let y = 15;

    doc.setFontSize(14);
    doc.text('Grouped Order Report', 14, y);
    y += 10;

    Object.keys(grouped).forEach(category => {
      doc.setFontSize(12);
      doc.text(`Category : ${category}`, 14, y);
      y += 6;

      Object.keys(grouped[category]).forEach(subCategory => {
        doc.setFontSize(11);
        doc.text(`Sub Category : ${subCategory}`, 16, y);
        y += 4;

        const tableRows = grouped[category][subCategory].map((item: any) => [
          item.productName,
          item.barcode,
          item.quantity
        ]);

        autoTable(doc, {
          startY: y,
          head: [['Product Name', 'Barcode', 'Quantity']],
          body: tableRows,
          margin: { left: 16 },
          styles: { fontSize: 9 }
        });

        y = (doc as any).lastAutoTable.finalY + 6;
      });

      y += 4;
    });

    doc.save('Grouped_Order_Report.pdf');
    this.toastr.success('Grouped PDF Downloaded');
  }

  downloadExcel() {
    let allItems: any[] = [];
    this.orders.forEach((order: any) => {
      order.orderItems?.forEach((item: any) => allItems.push(item));
    });

    if (allItems.length === 0) {
      this.toastr.warning('No data to export');
      return;
    }

    const grouped = this.groupItemsByCategory(allItems);
    const excelRows: any[] = [];

    Object.keys(grouped).forEach(category => {
      excelRows.push({ A: `Category : ${category}` });

      Object.keys(grouped[category]).forEach(subCategory => {
        excelRows.push({ A: `Sub Category : ${subCategory}` });
        excelRows.push({
          A: 'Product Name',
          B: 'Barcode',
          C: 'Quantity'
        });

        grouped[category][subCategory].forEach((item: any) => {
          excelRows.push({
            A: item.productName,
            B: item.barcode,
            C: item.quantity
          });
        });

        excelRows.push({});
      });

      excelRows.push({});
      excelRows.push({});
    });

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(excelRows, { skipHeader: true });
    worksheet['!cols'] = [
      { wch: 35 },
      { wch: 20 },
      { wch: 10 }
    ];

    const workbook: XLSX.WorkBook = {
      Sheets: { 'Grouped Report': worksheet },
      SheetNames: ['Grouped Report']
    };

    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buffer]), 'Grouped_Order_Report.xlsx');
    this.toastr.success('Grouped Excel Downloaded');
  }

  /* PRINT BILL */
    printBill(order: OrderDetails) {
      const encryptedBillId = CryptoJS.AES.encrypt(String(order.billId), this.secretKey).toString();
      const encryptedBuyerType = CryptoJS.AES.encrypt(String(order.buyerType), this.secretKey).toString();
      const encryptedScreenName = CryptoJS.AES.encrypt(String("order-processing"), this.secretKey).toString();
      this.router.navigate(['/bill'], { queryParams: { billId: encryptedBillId, buyerType: encryptedBuyerType,screenName : encryptedScreenName } });
    }

openImage(imageId: number): void {

  const imageUrl = this.getImageById(imageId);

  const newWindow = window.open('', '_blank');

  if (newWindow) {

    newWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
          <title>Image Preview</title>

          <style>

              html,body{
                  margin:0;
                  padding:0;
                  width:100%;
                  height:100%;
                  background:#111;
                  display:flex;
                  justify-content:center;
                  align-items:center;
                  overflow:hidden;
              }

              img{
                  max-width:100%;
                  max-height:100%;
                  object-fit:contain;
                  user-select:none;
              }

          </style>

      </head>

      <body>

          <img src="${imageUrl}" />

      </body>

      </html>
    `);

    newWindow.document.close();

  }

}

}